import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxEmailChipInput, {
    AcxEmailChipInputHandle,
} from "components/UI/AcxEmailChipInput";
import { observer } from "mobx-react";
import React, { useRef } from "react";
import ResearchFindingsStore from "stores/ResearchFindingsStore";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { useStore } from "utils/useStore";
import type ResearchFindingStore from "./ResearchFindingStore";

type Props = {
    store: ResearchFindingStore;
};

/** Client report email preview + multi-recipient send. */
const EmailPreviewDialog: React.FC<Props> = observer(({ store }) => {
    const theme = useTheme();
    const findingsStore = useStore(ResearchFindingsStore);
    const emailInputRef = useRef<AcxEmailChipInputHandle>(null);
    const open = !!store.emailQuestionId;
    const email = store.email;

    const handleSend = () => {
        // Commit any address still typed in the box so it is included.
        const finalRecipients =
            emailInputRef.current?.flushPending() ?? store.recipients;
        void store.sendEmail(finalRecipients);
    };

    return (
        <Dialog
            open={open}
            onClose={() => store.closeEmail()}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Client email preview</DialogTitle>
            <DialogContent dividers>
                {store.isEmailLoading ? (
                    <>
                        <Skeleton
                            variant="text"
                            width="60%"
                            height={24}
                            sx={{ backgroundColor: theme.palette.gray[300], mb: 1 }}
                        />
                        <Skeleton
                            variant="rounded"
                            height={200}
                            sx={{
                                backgroundColor: theme.palette.gray[300],
                                borderRadius: "8px",
                            }}
                        />
                        <Skeleton
                            variant="rounded"
                            height={40}
                            sx={{
                                backgroundColor: theme.palette.gray[300],
                                borderRadius: "8px",
                                mt: 2,
                            }}
                        />
                    </>
                ) : !email ? (
                    <Typography sx={{ color: theme.palette.gray[500] }}>
                        No email is available for this question.
                    </Typography>
                ) : (
                    <>
                        {email.subject && (
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    color: theme.palette.gray[700],
                                    mb: 1,
                                }}
                            >
                                {email.subject}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                border: `1px solid ${theme.palette.gray[200]}`,
                                borderRadius: "8px",
                                padding: theme.spacing(2),
                                maxHeight: "45vh",
                                overflow: "auto",
                                fontSize: "14px",
                                color: theme.palette.gray[700],
                            }}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(email.htmlBody),
                            }}
                        />
                        <Box sx={{ mt: 2 }}>
                            <AcxEmailChipInput
                                ref={emailInputRef}
                                values={store.recipients}
                                setValues={(values) =>
                                    store.setRecipients(values)
                                }
                                suggestions={findingsStore.emailSuggestions}
                                loading={findingsStore.isEmailSuggestionsLoading}
                                placeholderText="Add recipient email addresses…"
                                helperText="Enter one or more addresses (↵ or comma), or pick a teammate. Leave empty to send the report to yourself."
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <AcxButton
                    color="white"
                    fullWidth={false}
                    onClick={() => store.closeEmail()}
                >
                    Cancel
                </AcxButton>
                <AcxButton
                    color="primary"
                    fullWidth={false}
                    disabled={!email?.reportId || store.isSending}
                    loading={store.isSending}
                    onClick={handleSend}
                >
                    Send email
                </AcxButton>
            </DialogActions>
        </Dialog>
    );
});

export default EmailPreviewDialog;
